/**
 * ч.1105 (рішення власника): «Обліковий запис» → «Видалити організацію».
 * ЄДИНЕ місце, де організацію можна видалити. Видалення — фізичне і
 * безповоротне: документи, проводки, регістри, працівники, договори, рахунки,
 * вкладення цієї організації. Довідники акаунта (контрагенти, номенклатура,
 * фізособи) — спільні для організацій акаунта і лишаються.
 * Лише власник акаунта; пароль + назва вручну; для підтвердженої організації —
 * підпис ключем КЕП цієї організації.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { accountOrgs, type AccountOrg, type OrgRemoval } from '@/api/accountOrgs';
import { useToast } from '@/components/ToastProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import DestructiveConfirm from './DestructiveConfirm';

const ENTITY_LABEL: Record<string, string> = { LEGAL_ENTITY: 'юрособа', FOP: 'ФОП', INDIVIDUAL: 'фізособа' };
/** ч.1406: картка учасника ЕДО (без обліку) чи організація з обліком. */
export function orgKindLabel(o: Pick<AccountOrg, 'plan' | 'entity_type'>): string {
  const who = o.entity_type ? ENTITY_LABEL[o.entity_type] ?? '' : '';
  return o.plan === 'EDO' ? `картка ЕДО${who ? ` (${who})` : ''}` : `з обліком${who ? ` (${who})` : ''}`;
}

const KIND_LABEL: Record<OrgRemoval['kind'], string> = {
  DELETED: 'видалено', TRANSFERRED: 'передано', RECEIVED: 'прийнято',
};
const num = (n: number) => n.toLocaleString('uk-UA');

export function RemovalsJournal() {
  const q = useQuery({ queryKey: ['account-org-removals'], queryFn: accountOrgs.removals });
  const cols = useMemo<ColumnDef<OrgRemoval>[]>(() => [
    { key: 'date', header: 'Дата', width: 150, sortValue: (r) => r.created_at,
      cell: (r) => new Date(r.created_at).toLocaleString('uk-UA') },
    { key: 'kind', header: 'Подія', width: 100, sortValue: (r) => r.kind, cell: (r) => KIND_LABEL[r.kind] },
    { key: 'org', header: 'Організація', sortValue: (r) => r.organization_name,
      cell: (r) => `${r.organization_name}${r.organization_code ? ` — ${r.organization_code}` : ''}` },
    { key: 'who', header: 'Хто', width: 200, sortValue: (r) => r.performed_by_name ?? '',
      cell: (r) => r.performed_by_name ?? '' },
    { key: 'cp', header: 'Кому / від кого', width: 220, sortValue: (r) => r.counterparty ?? '',
      cell: (r) => r.counterparty ?? '' },
  ], []);
  if (!q.data?.length) return null;
  return (
    <div className="card">
      <div className="toolbar"><h2 style={{ margin: 0, fontSize: '1.05rem' }}>Журнал: видалені й передані організації</h2></div>
      <DataTable<OrgRemoval> rows={q.data} columns={cols} rowKey={(r) => r.id}
        initialSortKey="date" initialSortDir="desc" emptyText="—" />
    </div>
  );
}

export default function DeleteOrganizationSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selected, setSelected] = useState<AccountOrg | null>(null);
  const listQ = useQuery({ queryKey: ['account-orgs'], queryFn: accountOrgs.list });
  const previewQ = useQuery({
    queryKey: ['account-org-delete-preview', selected?.id],
    queryFn: () => accountOrgs.deletePreview(selected!.id, selected!.tenant_id),
    enabled: !!selected,
  });
  // ч.1406: перелік — по всіх власних акаунтах (з картками ЕДО); акаунт показуємо, коли їх кілька.
  const multiAccount = new Set((listQ.data ?? []).map((o) => o.tenant_id ?? '')).size > 1;

  const cols = useMemo<ColumnDef<AccountOrg>[]>(() => [
    { key: 'name', header: 'Організація', sortValue: (o) => o.name, cell: (o) => o.name },
    ...(multiAccount ? [{ key: 'account', header: 'Акаунт', width: 180, sortValue: (o: AccountOrg) => o.tenant_name ?? '',
      cell: (o: AccountOrg) => o.tenant_name ?? '' } as ColumnDef<AccountOrg>] : []),
    { key: 'kind', header: 'Вид', width: 170, sortValue: (o) => orgKindLabel(o), cell: (o) => orgKindLabel(o) },
    { key: 'code', header: 'ЄДРПОУ / РНОКПП', width: 150, sortValue: (o) => o.code ?? '', cell: (o) => o.code ?? '' },
    { key: 'docs', header: 'Документів', width: 110, align: 'right', sortValue: (o) => o.documents,
      cell: (o) => num(o.documents) },
    { key: 'state', header: 'Стан', width: 230, sortValue: (o) => Number(o.kep_confirmed),
      cell: (o) => (
        <>
          {o.kep_confirmed ? 'підтверджена ключем КЕП' : 'не підтверджена'}
          {o.deletion_mark && <span style={{ color: 'var(--muted)' }}> · позначена на видалення</span>}
        </>
      ) },
    { key: 'act', header: '', width: 110, align: 'center',
      cell: (o) => (
        <button type="button" className="btn-link" onClick={() => setSelected(o)}>видалити…</button>
      ) },
  ], [multiAccount]);

  const pv = previewQ.data;
  const done = async () => {
    setSelected(null);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['account-orgs'] }),
      qc.invalidateQueries({ queryKey: ['account-org-removals'] }),
      qc.invalidateQueries({ queryKey: ['orgs'] }),
      qc.invalidateQueries({ queryKey: ['orgs-for-modules'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
      qc.invalidateQueries({ queryKey: ['attachments-storage-usage'] }),
    ]);
  };

  return (
    <>
      <div className="card">
        <div className="toolbar" style={{ alignItems: 'flex-start' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Видалити організацію</h2>
            {/* ч.1406 (рішення власника): без довгої підказки — лише суть. */}
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              Організація буде видалена безповоротно.
            </div>
          </div>
        </div>
        <DataTable<AccountOrg> rows={listQ.data ?? []} columns={cols} rowKey={(o) => o.id}
          loading={listQ.isLoading} emptyText="У ваших акаунтах немає організацій."
          rowBold={(o) => o.id === selected?.id} />
      </div>

      {selected && (
        <div className="card" data-testid="delete-org-panel">
          <div className="toolbar">
            <h2 style={{ margin: 0, fontSize: '1.05rem' }}>Видалення: {selected.name}</h2>
            <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setSelected(null)}>скасувати</button>
          </div>
          {previewQ.isLoading && <p>Рахуємо, що буде видалено…</p>}
          {previewQ.isError && <p style={{ color: 'var(--badge-red-fg)' }}>Не вдалося підготувати видалення.</p>}
          {pv && (
            <>
              <p style={{ marginTop: 0 }}>
                Буде видалено <b>{num(pv.rows)}</b> записів
                {pv.highlights.length > 0 && <> — зокрема {pv.highlights.map((h) => `${h.label}: ${num(h.count)}`).join(', ')}</>}
                {pv.files > 0 && <>; файлів вкладень: <b>{num(pv.files)}</b></>}.
              </p>
              {pv.blockers.length > 0 && (
                <p style={{ color: 'var(--badge-red-fg)' }}>
                  Видалення неможливе: на дані організації посилаються інші дані акаунта
                  ({pv.blockers.map((b) => b.table).join(', ')}). Зверніться в підтримку.
                </p>
              )}
              <DestructiveConfirm
                action="org-delete" org={selected} kepRequired={pv.kep_required}
                submitLabel="видалити організацію назавжди" busyLabel="видаляємо…"
                disabled={pv.blockers.length > 0}
                disabledTitle="Є обовʼязкові посилання на дані організації"
                onSubmit={async (payload) => {
                  const res = await accountOrgs.remove(selected.id, payload, selected.tenant_id);
                  toast.show({ type: 'success', durationMs: 9000,
                    message: `Організацію «${res.name}» видалено: записів ${num(res.rows)}, файлів ${num(res.files)}.` });
                  await done();
                }}
              />
            </>
          )}
        </div>
      )}
      <RemovalsJournal />
    </>
  );
}
